import React from "react";
import { ShieldCheck } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

type ConfirmDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  headerTitle?: string;
  HeaderIcon?: React.ComponentType<any>;
  ContentIcon?: React.ComponentType<any>;
  title: React.ReactNode;
  description?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void | Promise<void>;
  // Customization
  confirmVariant?:
    | "default"
    | "destructive"
    | "outline"
    | "secondary"
    | "ghost"
    | "link"
    | "success";
  cancelVariant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link";
  confirmClassName?: string;
  cancelClassName?: string;
  contentIconBgClass?: string;
  contentIconColorClass?: string;
  onCancel?: () => void;
};

export function ConfirmDialog({
  open,
  onOpenChange,
  headerTitle,
  HeaderIcon = ShieldCheck,
  ContentIcon,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  onConfirm,
  confirmVariant = "default",
  cancelVariant = "outline",
  confirmClassName,
  cancelClassName,
  contentIconBgClass = "cdms-primary-soft",
  contentIconColorClass = "text-primary",
  onCancel,
}: ConfirmDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <div className="flex flex-col items-stretch gap-4 p-4 md:p-5">
          {/* Optional header: left icon + title (prominent) */}
          {headerTitle ? (
            <>
              <div className="flex items-center gap-3 -mt-1">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-sidebar-primary text-sidebar-primary-foreground shadow-sm">
                  <HeaderIcon className="h-5 w-5" />
                </div>
                <div className="text-xl font-extrabold tracking-wide">{headerTitle}</div>
              </div>

              <div className="border-t" />
            </>
          ) : null}

          {/* Content: 2-column layout with icon left and centered text on right */}
          <div className="grid grid-cols-[64px_1fr] items-start gap-4">
            {ContentIcon ? (
              <div
                className={
                  "flex h-14 w-14 items-center justify-center rounded-full shadow-sm " +
                  contentIconBgClass +
                  " " +
                  contentIconColorClass
                }
              >
                <ContentIcon className="h-7 w-7" />
              </div>
            ) : (
              <div />
            )}

            <div className="flex flex-col items-start text-left">
              <DialogTitle className="text-lg font-semibold">{title}</DialogTitle>
              {description ? (
                <DialogDescription className="text-sm text-muted-foreground mt-2 max-w-[28rem]">
                  {description}
                </DialogDescription>
              ) : null}
            </div>
          </div>

          <DialogFooter className="flex justify-end gap-3 mt-2">
            <DialogClose asChild>
              <Button
                variant={cancelVariant as any}
                size="default"
                className={"px-4 " + (cancelClassName ?? "")}
                onClick={() => onCancel?.()}
              >
                {cancelLabel}
              </Button>
            </DialogClose>
            <Button
              variant={confirmVariant as any}
              size="default"
              onClick={() => {
                // Close then trigger confirm
                onOpenChange(false);
                void onConfirm();
              }}
              className={"px-4 " + (confirmClassName ?? "")}
            >
              {confirmLabel}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ConfirmDialog;
